import classes from "./Bank.module.scss";
import PropTypes from "prop-types";
import {
  motion,
  useAnimation,
  useAnimate,
  useMotionValue,
  animate,
} from "framer-motion";
import clsx from "clsx";
import { forwardRef, useEffect, useState, useImperativeHandle } from "react";
import {
  CONTENT_TYPE,
  NAME_SOUND_ACTIVE_BANK,
  sizeCalc,
  useGameSounds,
} from "@educaplay/core";
import { IconButton } from "@educaplay/core/components";
import { Search } from "@educaplay/core/Icons/Search";
import { useSelector } from "react-redux";
import { useCustomHandlers } from "../CustomHandlersProvider/useCustomHandlers";
import { Dialog } from "@educaplay/core/components";
import { InfoBank } from "../InfoBank";
import { YouTubeComponent } from "@educaplay/core/components";
import { AudioComponentControl } from "../../components/AudioComponentControl";
import { Slot } from "@educaplay/core/components";

/**
 * @param {array} content - content of all of the questions.
 * @param {number} index - actual question.
 * @param {React.node} infoBank - component infobank.
 * @returns {React.ReactNode} - return Bank.
 */
export const Bank = forwardRef(function Bank(props, ref) {
  const { animated, className, hasBank } = props;

  const {
    renders: { infoLeft, infoRight },
  } = useCustomHandlers();
  const gameSounds = useGameSounds();
  const animationControls = useAnimation();
  const [bankRef] = useAnimate();
  const [openImage, setOpenImage] = useState(false);
  const [animateEntryHasFinished, setAnimateEntryHasFinished] = useState(false);

  const isBankHidden = useSelector((state) => state.game.isBankHidden);

  const questions = useSelector((state) => state.game.questions);
  const currentIndex = useSelector((state) => state.game.currentIndex);
  const showBankControl = useSelector((state) => state.game.showBankControl);
  const [currentContent, setCurrentContent] = useState(null);
  const [auxiliarShowBank, setAuxiliarShowBank] = useState(showBankControl);
  const titleSize = currentContent?.title?.length ?? 0;
  const descriptionSize = currentContent?.text?.length ?? 0;
  const size = titleSize + descriptionSize;
  const sizeClass = sizeCalc(size);
  const y = useMotionValue(animated ? "-150%" : 0);
  const hasContent = !!(
    currentContent?.text ||
    currentContent?.title ||
    currentContent?.source
  );
  const hasAdditionalText = currentContent?.text || currentContent?.title;

  const alingCenter =
    (currentContent?.text?.length ?? 0) < 250 && !currentContent?.source;

  const hasBankContent =
    (hasBank && hasContent && auxiliarShowBank) ||
    (auxiliarShowBank && hasContent);

  useImperativeHandle(ref, () => ({
    animateEntry: async (options = {}) => {
      setCurrentContent(questions[currentIndex].bank);
      const contentSelected = questions[currentIndex].bank;
      if (
        contentSelected?.text ||
        contentSelected?.title ||
        contentSelected?.source
      ) {
        gameSounds.play(NAME_SOUND_ACTIVE_BANK);
      }
      const duration = hasBankContent ? 0.5 : 0;
      await animate(y, 0, { type: "spring", duration, ...options });
      setAnimateEntryHasFinished(true);
    },
    animateExit: async (options = {}) => {
      await animate(y, "-180%", { type: "spring", ...options });
    },
  }));

  const OpenImage = () => {
    setOpenImage(true);
  };
  useEffect(() => {
    const nextContent = questions[currentIndex]?.bank;
    const hasNextContent = !!(
      nextContent?.text ||
      nextContent?.title ||
      nextContent?.source
    );

    if (!hasNextContent && !hasContent) {
      return;
    }

    const initAnimation = async () => {
      if (!animateEntryHasFinished) {
        setCurrentContent(nextContent);
        setAuxiliarShowBank(showBankControl);
        return;
      }

      await animate(y, "-180%", {
        transition: { duration: 0.5, ease: "easeInOut" },
      });

      setAuxiliarShowBank(showBankControl);

      setCurrentContent(nextContent);

      if (hasNextContent) {
        gameSounds.play(NAME_SOUND_ACTIVE_BANK);
      }

      await animate(y, 0, {
        transition: { duration: 0.5, delay: 0.15, type: "spring", bounce: 0.5 },
      });
    };

    initAnimation();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentIndex, animationControls, showBankControl]);

  return (
    <motion.div
      style={{ y }}
      ref={bankRef}
      className={clsx(classes.wrapper, className)}
      onMouseDown={(e) => {
        e.preventDefault();
      }} // previene que si algo del juego tiene el foco, al hacer click en el banco, no se pierda el foco del juego
    >
      <motion.div
        style={{ y: 0 }}
        animate={animationControls}
        className={classes["sub-header"]}
      >
        <div
          className={clsx(classes.question, {
            [classes.color]: !isBankHidden && hasBankContent,
          })}
        >
          <InfoBank
            infoLeft={infoLeft?.(currentIndex, questions)}
            infoRight={infoRight?.(currentIndex, questions)}
          />

          <Slot name="core-custom-bank" />

          {!isBankHidden && (
            <>
              {hasBankContent && (
                <div
                  className={clsx(
                    classes.question__content,
                    !infoLeft && !infoRight && classes.outpadding,
                    classes[sizeClass]
                  )}
                >
                  {currentContent?.type === CONTENT_TYPE.IMAGE && (
                    <>
                      <div className={classes.containerImg}>
                        <div className={classes.sourceImg}>
                          <Dialog
                            open={openImage}
                            onClose={() => setOpenImage(false)}
                            variant="lightbox"
                          >
                            <img src={currentContent?.source} alt="" />
                          </Dialog>
                          <img
                            src={currentContent?.source}
                            onClick={OpenImage}
                          />
                          <IconButton
                            icon={<Search  />}
                            onClick={OpenImage}
                            className={classes.expand}
                          />
                        </div>
                      </div>
                      {hasAdditionalText && (
                        <div
                          className={clsx(
                            classes.containertext,
                            (titleSize > 50 || descriptionSize > 180) &&
                              classes.containertextscroll,
                            classes.scrollbar,
                            alingCenter && classes.alingCenter
                          )}
                        >
                          {currentContent?.title && (
                            <p className={classes.title}>
                              {currentContent?.title}
                            </p>
                          )}
                          <p>{currentContent?.text}</p>
                        </div>
                      )}
                    </>
                  )}

                  {currentContent?.type === CONTENT_TYPE.AUDIO && (
                    <>
                      <div className={classes.audioSrc}>
                        <AudioComponentControl
                          key={currentIndex}
                          source={currentContent?.source}
                          autoplay
                        />
                      </div>
                      {hasAdditionalText && (
                        <div
                          className={clsx(
                            classes.containertext,
                            (titleSize > 50 || descriptionSize > 180) &&
                              classes.containertextscroll,
                            classes.scrollbar,
                            alingCenter && classes.alingCenter
                          )}
                        >
                          {currentContent?.title && (
                            <p className={classes.title}>
                              {currentContent?.title}
                            </p>
                          )}
                          <p>{currentContent?.text}</p>
                        </div>
                      )}
                    </>
                  )}

                  {currentContent?.type === CONTENT_TYPE.YOUTUBE && (
                    <>
                      <div className={classes.containerYoutube}>
                        <YouTubeComponent url={currentContent?.source} />
                      </div>
                      {hasAdditionalText && (
                        <div
                          className={clsx(
                            classes.containertext,
                            (titleSize > 50 || descriptionSize > 180) &&
                              classes.containertextscroll,
                            classes.scrollbar,
                            alingCenter && classes.alingCenter
                          )}
                        >
                          {currentContent?.title && (
                            <p className={classes.title}>
                              {currentContent?.title}
                            </p>
                          )}
                          <p>{currentContent?.text}</p>
                        </div>
                      )}
                    </>
                  )}

                  {currentContent?.type === CONTENT_TYPE.TEXT && (
                    <div
                      className={clsx(
                        classes.containertext,
                        (titleSize > 50 || descriptionSize > 180) &&
                          classes.containertextscroll,
                        classes.scrollbar,
                        alingCenter && classes.alingCenter
                      )}
                    >
                      {currentContent?.title && (
                        <p className={classes.title}>{currentContent?.title}</p>
                      )}
                      <p>{currentContent?.text}</p>
                    </div>
                  )}
                </div>
              )}
            </>
          )}
        </div>
      </motion.div>
    </motion.div>
  );
});

Bank.propTypes = {
  content: PropTypes.shape({
    text: PropTypes.string,
    source: PropTypes.string,
  }),
  contentType: PropTypes.string,
  showQuestion: PropTypes.bool,
  index: PropTypes.number,
  infoBank: PropTypes.node,
  animated: PropTypes.bool,
  className: PropTypes.string,
  hasBank: PropTypes.bool,
};
